import { Devices, enumKeys, Facilities, getTargetTypeDisplayName, MaxServerIntValue, TargetType } from "@app/shared";
import { useFetch } from "@app/shared/src/hooks";
import { Input, InputRow, NumericInput, Select } from "@app/dashboard/src/components/shared/inputs";
import { BiuDetectorRowData, getTriggerTypeConfig, TRIGGER_TYPE_OPTIONS } from "./types";
import { DetectionZoneDwellEventTrigger, DetectionZonePresenceEventTrigger, ImpendingZoneEntryEventTrigger, SpeedingEventTrigger, WrongWayEventTrigger } from "@app/shared";

type BiuDetectorEditorProps = {
    organizationId: string;
    facilityId?: string;
    data: BiuDetectorRowData;
    onChange: (data: Partial<BiuDetectorRowData>) => void;
    onSave: () => void;
    onCancel: () => void;
    onDelete?: () => void;
    isNew?: boolean;
    offlineMode?: boolean;
};

const AllTargetTypes = enumKeys(TargetType)
    .sort((a, b) => a.localeCompare(b))
    .filter((k) => k !== "Unknown");

export const BiuDetectorEditor: React.FC<BiuDetectorEditorProps> = ({ organizationId, facilityId, data, onChange, onSave, onCancel, onDelete, isNew, offlineMode }) => {
    const triggerConfig = getTriggerTypeConfig(data.triggerType);
    const hasDetectionZone = triggerConfig?.hasDetectionZone ?? false;
    const hasScreenLine = triggerConfig?.hasScreenLine ?? false;

    const [detectionZones] = useFetch(() => (hasDetectionZone ? Facilities.getDetectionZoneOptions(organizationId, facilityId) : Promise.resolve([])), [hasDetectionZone, organizationId, facilityId]);

    const [screenLines] = useFetch(() => (hasScreenLine ? Facilities.getScreenLineOptions(organizationId, facilityId) : Promise.resolve([])), [hasScreenLine, organizationId, facilityId]);

    const [deviceOptions] = useFetch(() => Devices.getOptions(organizationId, facilityId), [organizationId, facilityId]);

    const selectedDeviceId = data.deviceIds?.[0] ?? (offlineMode && deviceOptions?.length === 1 ? deviceOptions[0].value : "");
    const isDeviceSelected = !!selectedDeviceId || (offlineMode && deviceOptions?.length === 1);
    const isTriggerTypeSelected = !!data.triggerType;
    const isExpressionFilled = !!data.expression?.trim();
    const canSave = isDeviceSelected && isTriggerTypeSelected && isExpressionFilled;

    const handleTriggerTypeChange = (newType: string) => {
        onChange({
            triggerType: newType,
            detectionZoneId: undefined,
            detectionZoneName: undefined,
            screenLineId: undefined,
            screenLineName: undefined,
        });
    };

    return (
        <div className="bg-gray-50 p-4 border-t border-gray-200 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <InputRow>
                    <Input label="Evaluation Expression (Required)" value={data.expression ?? ""} onChange={(e) => onChange({ expression: e.target.value })} placeholder="e.g., e.EventValue >= 4" />
                </InputRow>

                <InputRow>
                    <Select label="Trigger Type (Required)" emptyOptionText="Select Trigger Type" emptyOptionDisabled={true} options={TRIGGER_TYPE_OPTIONS.map((t) => ({ label: t.label, value: t.$type }))} value={data.triggerType ?? ""} onChange={(e) => handleTriggerTypeChange(e.target.value)} />
                </InputRow>

                {hasDetectionZone && (
                    <InputRow>
                        <Select label="Detection Zone Filter" emptyOptionText="Any Detection Zone" emptyOptionDisabled={false} optionGroups={detectionZones} value={data.detectionZoneId ?? ""} onChange={(e) => onChange({ detectionZoneId: e.target.value || undefined, detectionZoneName: undefined })} />
                    </InputRow>
                )}

                {hasScreenLine && (
                    <InputRow>
                        <Select label="Screen Line Filter" emptyOptionText="Any Screen Line" emptyOptionDisabled={false} optionGroups={screenLines} value={data.screenLineId ?? ""} onChange={(e) => onChange({ screenLineId: e.target.value || undefined, screenLineName: undefined })} />
                    </InputRow>
                )}

                {!offlineMode && deviceOptions && (
                    <InputRow>
                        <Select label="Device (Required)" emptyOptionText="Select a Device" emptyOptionDisabled={true} options={deviceOptions.map((d) => ({ label: d.label, value: d.value }))} value={selectedDeviceId} onChange={(e) => onChange({ deviceIds: e.target.value ? [e.target.value] : [] })} />
                    </InputRow>
                )}
            </div>

            {data.triggerType === DetectionZoneDwellEventTrigger.$type && (
                <InputRow>
                    <NumericInput allowFloat={false} label="Minimum Dwell Time (s)" value={data.minValue} onChange={(e) => onChange({ minValue: e })} />
                </InputRow>
            )}

            {data.triggerType === DetectionZonePresenceEventTrigger.$type && (
                <div className="grid grid-cols-2 gap-4">
                    <InputRow>
                        <NumericInput allowFloat={false} label="Minimum Targets" value={data.minValue} onChange={(e) => onChange({ minValue: e })} />
                    </InputRow>
                    <InputRow>
                        <NumericInput allowFloat={false} label="Min Dwell Time (ms)" value={data.additionalValue} onChange={(e) => onChange({ additionalValue: e })} />
                    </InputRow>
                </div>
            )}

            {data.triggerType === ImpendingZoneEntryEventTrigger.$type && (
                <div className="grid grid-cols-2 gap-4">
                    <InputRow>
                        <NumericInput label="Driver Reaction Time" value={data.minValue} onChange={(e) => onChange({ minValue: e })} />
                    </InputRow>
                    <InputRow>
                        <NumericInput label="Minimum Vehicle Speed" value={data.additionalValue} onChange={(e) => onChange({ additionalValue: e })} />
                    </InputRow>
                </div>
            )}

            {data.triggerType === SpeedingEventTrigger.$type && (
                <div className="grid grid-cols-2 gap-4">
                    <InputRow>
                        <NumericInput label="Minimum Speed" value={data.minValue} onChange={(e) => onChange({ minValue: e })} debounce={0} />
                    </InputRow>
                    <InputRow>
                        <NumericInput label="Maximum Speed" value={data.maxValue} onChange={(e) => onChange({ maxValue: e })} debounce={0} />
                    </InputRow>
                </div>
            )}

            {data.triggerType === WrongWayEventTrigger.$type && (
                <InputRow>
                    <NumericInput label="Heading Tolerance (deg)" value={data.additionalValue} onChange={(e) => onChange({ additionalValue: e })} />
                </InputRow>
            )}

            <div className="border-t border-gray-200 pt-4">
                <div className="font-medium text-sm text-gray-700 mb-2">Target Filters:</div>
                <div className="flex flex-wrap gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" className="w-4 h-4" checked={data.targetFilter === MaxServerIntValue} onChange={(e) => onChange({ targetFilter: e.target.checked ? MaxServerIntValue : 0 })} />
                        <span className="text-sm">All Targets</span>
                    </label>
                    {AllTargetTypes.map((t) => (
                        <label key={t} className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                className="w-4 h-4"
                                checked={((data.targetFilter ?? 0) & TargetType[t]) > 0}
                                onChange={(e) => {
                                    const currentFilter = data.targetFilter ?? 0;
                                    const newFilter = e.target.checked ? currentFilter | TargetType[t] : currentFilter ^ TargetType[t];
                                    onChange({ targetFilter: newFilter });
                                }}
                            />
                            <span className="text-sm">{getTargetTypeDisplayName(t)}</span>
                        </label>
                    ))}
                </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                {!isNew && onDelete && (
                    <button type="button" onClick={onDelete} className="px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-300 rounded-md hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500">
                        Delete
                    </button>
                )}
                <button type="button" onClick={onCancel} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500">
                    Cancel
                </button>
                <button type="button" onClick={onSave} disabled={!canSave} className="px-4 py-2 text-sm font-medium text-white bg-brand-primary border border-transparent rounded-md hover:bg-brand-primary-dark focus:outline-none focus:ring-2 focus:ring-brand-primary disabled:opacity-50 disabled:cursor-not-allowed">
                    {isNew ? "Add" : "Save"}
                </button>
            </div>
        </div>
    );
};
